import React from 'react';
import { z } from 'zod';
import { safeUrl } from '../url';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// Article grid — faithful rewrite of legacy molecules/InsightsMainLayout +
// InsightsCards (the card grid on insights/news/solution-brief listing pages).
// "Stay Updated" header over a 3-up grid of cards: feature image, read-time,
// blue title, excerpt; each links to the entry.
export const articleGridSchema = z.object({
  eyebrow: z.string().optional(),
  heading: z.string().min(1),
  items: z
    .array(
      z.object({
        minuteRead: z.string().optional(),
        title: z.string().min(1),
        excerpt: z.string().optional(),
        imageUrl: z.string().optional(),
        url: safeUrl,
      }),
    )
    .default([]),
});

type ArticleGridProps = z.infer<typeof articleGridSchema>;

const Section = styled.section`
  max-width: 112rem;
  margin: 0 auto;
  padding: 4rem 1.6rem;

  @media (min-width: 48em) {
    padding: 6rem 1.6rem;
  }
`;

const Eyebrow = styled.p`
  margin: 0;
  color: #1a1b1f;
  font-family: 'DM Sans', sans-serif;
  font-weight: 400;
  font-size: 1.4rem;
  letter-spacing: 5px;
  text-transform: uppercase;

  @media (min-width: 48em) {
    font-size: 1.7rem;
  }
`;

const Heading = styled.h2`
  margin: 0.8rem 0 0;
  color: #1a1b1f;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: 2.6rem;
  max-width: 65rem;

  @media (min-width: 48em) {
    font-size: 4.8rem;
    letter-spacing: -2px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  column-gap: 2.4rem;
  row-gap: 4rem;
  margin-top: 3rem;

  @media (min-width: 48em) {
    grid-template-columns: repeat(3, 1fr);
    margin-top: 4rem;
  }
`;

const Card = styled.a`
  display: block;
  text-decoration: none;
`;

const Thumb = styled.img`
  width: 100%;
  height: 25rem;
  border-radius: 0.8rem;
  object-fit: cover;
  background: #eef3ff;
`;

const ReadTime = styled.p`
  margin: 1rem 0 0;
  color: #4e4f52;
  font-family: 'DM Sans', sans-serif;
  font-weight: 400;
  font-size: 1.4rem;
`;

const CardTitle = styled.p`
  margin: 0.4rem 0 0;
  color: #0a3aa8;
  font-family: 'DM Sans', sans-serif;
  font-weight: 600;
  font-size: 1.8rem;
`;

const CardExcerpt = styled.p`
  margin: 0.6rem 0 0;
  color: #1a1b1f;
  font-family: 'DM Sans', sans-serif;
  font-weight: 400;
  font-size: 1.6rem;
  line-height: 1.6;
`;

function ArticleGrid({ eyebrow, heading, items }: ArticleGridProps) {
  return (
    <Section>
      <Eyebrow>{eyebrow ?? 'Stay Updated'}</Eyebrow>
      <Heading>{heading}</Heading>
      <Grid>
        {items.map((item, i) => (
          <Card key={`${item.title}-${i}`} href={item.url}>
            {item.imageUrl ? <Thumb src={item.imageUrl} alt="" /> : null}
            {item.minuteRead ? <ReadTime>{item.minuteRead} min read</ReadTime> : null}
            <CardTitle>{item.title}</CardTitle>
            {item.excerpt ? <CardExcerpt>{item.excerpt.slice(0, 200)}…</CardExcerpt> : null}
          </Card>
        ))}
      </Grid>
    </Section>
  );
}

export const articleGridBlock: BlockDefinition<ArticleGridProps> = {
  type: 'articleGrid',
  schema: articleGridSchema,
  Renderer: ArticleGrid,
};
